import { useCallback, useEffect, useRef } from 'react';

/**
 * Publie la hauteur réelle de l'en-tête épinglé dans la variable CSS
 * `--hauteur-entete`. Les éléments collants des pages (bandeau du panneau de
 * contact) et le défilement vers une ancre s'en servent pour ne pas se glisser
 * sous l'en-tête. La hauteur change avec la largeur de la fenêtre et avec le
 * nombre de chiffres affichés, d'où une mesure continue plutôt qu'une constante.
 *
 * Renvoie une référence de rappel à poser sur l'élément `<header>`.
 */
export function useHauteurEntete(): (element: HTMLElement | null) => void {
  const observateur = useRef<ResizeObserver | null>(null);

  useEffect(() => () => observateur.current?.disconnect(), []);

  return useCallback((element: HTMLElement | null) => {
    observateur.current?.disconnect();
    observateur.current = null;
    if (!element) return;
    // Une hauteur nulle veut dire « pas encore mis en page » (ou jsdom) : on
    // laisse alors la valeur de repli de la feuille de style plutôt que 0.
    const publier = () => {
      if (element.offsetHeight > 0)
        document.documentElement.style.setProperty('--hauteur-entete', `${element.offsetHeight}px`);
    };
    publier();
    // Absent de jsdom : la mesure initiale suffit alors.
    if (typeof ResizeObserver === 'undefined') return;
    observateur.current = new ResizeObserver(publier);
    observateur.current.observe(element);
  }, []);
}
